// Currency Cache Service - Fetches and caches currency data from database
import { supabase } from '../config/supabase';

class CurrencyCacheService {
  constructor() {
    this.currencyCache = null;
    this.lastFetch = 0;
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Get all currencies from database with caching
  async getCurrencies() {
    try {
      const now = Date.now();
      
      // Return cached data if still valid
      if (this.currencyCache && (now - this.lastFetch) < this.cacheTimeout) {
        return this.currencyCache;
      }

      // Fetch fresh data from database
      const { data, error } = await supabase
        .from('countries')
        .select('currency, currency_symbol, name')
        .not('currency', 'is', null)
        .not('currency', 'eq', '')
        .order('currency');

      if (error) {
        console.error('Error fetching currencies:', error);
        return this.getFallbackCurrencies();
      }

      // Build currency cache
      const currencyMap = {};
      data.forEach(country => {
        if (country.currency && country.currency_symbol) {
          currencyMap[country.currency] = {
            symbol: country.currency_symbol,
            code: country.currency,
            country: country.name,
            decimals: this.getDecimalsForCurrency(country.currency)
          };
        }
      });

      // Update cache
      this.currencyCache = currencyMap;
      this.lastFetch = now;

      return currencyMap;
    } catch (error) {
      console.error('Error in getCurrencies:', error);
      return this.getFallbackCurrencies();
    }
  }

  // Get currency symbol from cache
  getCurrencySymbol(currency) {
    if (!this.currencyCache || !currency) {
      return '₨'; // Fallback to PKR
    }

    const currencyData = this.currencyCache[currency];
    return currencyData?.symbol || '₨';
  }

  // Get currency decimals from cache
  getCurrencyDecimals(currency) {
    if (!this.currencyCache || !currency) {
      return 0; // Default to 0 decimals
    }

    const currencyData = this.currencyCache[currency];
    return currencyData?.decimals || 0;
  }

  // Format currency with cached data - shows currency code in English (e.g., PKR, KWD, AED)
  formatCurrency(amount, currency) {
    const currencyCode = currency || 'PKR'; // Use currency code in English
    const decimals = this.getCurrencyDecimals(currencyCode);
    const numericAmount = Number(amount) || 0;

    if (decimals === 0) {
      return `${currencyCode} ${Math.round(numericAmount).toLocaleString()}`;
    }

    return `${currencyCode} ${numericAmount.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })}`;
  }

  // Get decimals for currency (helper function)
  getDecimalsForCurrency(currency) {
    const decimalMap = {
      'PKR': 0,
      'INR': 0,
      'BDT': 0,
      'JPY': 0,
      'KWD': 3,
      'BHD': 3,
      'OMR': 3,
      'JOD': 3,
      'USD': 2,
      'AED': 2,
      'SAR': 2,
      'GBP': 2,
      'EUR': 2,
      'CAD': 2,
      'AUD': 2,
      'CHF': 2,
      'SEK': 2,
      'NOK': 2,
      'DKK': 2,
      'PLN': 2,
      'CZK': 2,
      'HUF': 2,
      'RON': 2,
      'BGN': 2,
      'HRK': 2,
      'RSD': 2,
      'MKD': 2,
      'ALL': 2,
      'BAM': 2,
      'TRY': 2,
      'RUB': 2,
      'UAH': 2,
      'BYN': 2,
      'MDL': 2,
      'GEL': 2,
      'AMD': 2,
      'AZN': 2,
      'KZT': 2,
      'KGS': 2,
      'TJS': 2,
      'TMT': 2,
      'UZS': 2,
      'MYR': 2,
      'THB': 2,
      'SGD': 2,
      'IDR': 2,
      'PHP': 2,
      'VND': 2,
      'KRW': 0,
      'CNY': 2,
      'HKD': 2,
      'TWD': 2,
      'MOP': 2,
      'BND': 2,
      'LAK': 2,
      'KHR': 2,
      'MMK': 2,
      'GHS': 2,
      'NGN': 2,
      'ZAR': 2,
      'KES': 2,
      'EGP': 2,
      'MAD': 2,
      'TND': 2,
      'DZD': 2,
      'LYD': 2,
      'SDG': 2,
      'ETB': 2,
      'UGX': 0,
      'TZS': 2,
      'RWF': 0,
      'BIF': 0,
      'DJF': 0,
      'SOS': 2,
      'ERN': 2,
      'SLL': 2,
      'GMD': 2,
      'GNF': 0,
      'LRD': 2,
      'SLE': 2,
      'CDF': 2,
      'AOA': 2,
      'ZMW': 2,
      'BWP': 2,
      'SZL': 2,
      'LSL': 2,
      'NAD': 2,
      'MZN': 2,
      'MWK': 2,
      'ZWL': 2,
      'BZD': 2,
      'BBD': 2,
      'TTD': 2,
      'JMD': 2,
      'BMD': 2,
      'BSD': 2,
      'KYD': 2,
      'AWG': 2,
      'ANG': 2,
      'SRD': 2,
      'GYD': 2,
      'VES': 2,
      'COP': 2,
      'PEN': 2,
      'BOB': 2,
      'CLP': 0,
      'ARS': 2,
      'UYU': 2,
      'PYG': 0,
      'BRL': 2,
      'FKP': 2,
      'FKP': 2,
      'GYD': 2,
      'SRD': 2,
      'VES': 2,
      'COP': 2,
      'PEN': 2,
      'BOB': 2,
      'CLP': 0,
      'ARS': 2,
      'UYU': 2,
      'PYG': 0,
      'BRL': 2
    };
    return decimalMap[currency] || 2;
  }

  // Fallback currencies if database fails
  getFallbackCurrencies() {
    return {
      PKR: { symbol: '₨', code: 'PKR', country: 'Pakistan', decimals: 0 }
    };
  }

  // Clear cache (useful for testing or manual refresh)
  clearCache() {
    this.currencyCache = null;
    this.lastFetch = 0;
  }
}

export const currencyCacheService = new CurrencyCacheService();
export default currencyCacheService;
